import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useJobs, useJob, useActiveRenders, jobIsActive, cancelJob, pauseJob, resumeJob, deleteJob, clearErroredJobs } from '../data/firestore';
import { jobActualCost, fmtUSD } from '../utils/cost';
import { renderJobLabel, renderJobProgress } from '../utils/eta';
import JobFanoutGraph from '../components/JobFanoutGraph';

// Inline asset renders (still grab / develop / grade) baked on the always-on task.
const RENDER_TYPE_LABEL = { still: 'Frame grab → still', develop: 'RAW develop', grade: 'Grade export' };

const TYPE_LABEL = {
  'r3d-proxy': 'RED decode → HLS proxy',
  'r3d-master': 'RAW re-decode (publish)',
  publish: 'Publish · HLS + master encode',
};

// Fan-out jobs run on ephemeral per-chunk runner tasks (prep → chunks → stitch)
// and get the React-Flow graph; a `publish` job is a single wallclock encode on
// the always-on task, so it shows a plain progress bar instead.
const isFanout = (job) => job?.type === 'r3d-proxy' || job?.type === 'r3d-master';

// Friendly label for the prep task's internal sub-stage (so the row shows
// "Downloading source" instead of a flat 0% while the .RDC streams to scratch).
const PREP_STAGE_LABEL = { download: 'Downloading source', probe: 'Probing source', audio: 'Reading audio' };
const nf = (n) => Number(n || 0).toLocaleString();

// Derive a continuous, always-moving progress + meaningful status/throughput from
// the job doc and its per-task telemetry. The bar is banded so each phase shows
// motion: prep 0–10%, decode 10–95%, stitch 95–100%.
function deriveLive(job, tasks) {
  const chunks = tasks.filter((t) => t.role === 'chunk');
  const prep = tasks.find((t) => t.role === 'prep');
  const stitch = tasks.find((t) => t.role === 'stitch');
  const running = tasks.filter((t) => t.status === 'running' || t.status === 'launching').length;
  const plannedChunks = job.chunks || chunks.length || 0;
  const chunksDone = chunks.filter((t) => t.status === 'done').length;
  const framesDone = chunks.reduce((s, t) => s + (t.framesDone || 0), 0);
  const framesTotal = job.framesTotal || chunks.reduce((s, t) => s + (t.framesTotal || 0), 0);
  const fps = chunks.reduce((s, t) => s + (t.status === 'running' && t.fps ? t.fps : 0), 0);

  const preparing = job.status === 'planning' || job.stage === 'prep';
  const stitching = job.stage === 'stitching';

  let label;
  let bar = null;
  let sub = '';
  if (job.paused) {
    label = 'paused';
  } else if (job.status === 'done') {
    label = 'done';
    bar = 100;
  } else if (job.status === 'error') {
    label = 'error';
  } else if (job.status === 'cancelled') {
    label = 'cancelled';
  } else if (job.type === 'publish') {
    label = 'Publishing';
    bar = Math.round((job.progress || 0) * 100);
    sub = 'HLS ladder + master';
  } else if (preparing) {
    label = PREP_STAGE_LABEL[prep?.stage] || 'Preparing';
    bar = Math.round((prep?.progress || 0) * 10);
    sub = prep?.stage === 'download' ? 'streaming .RDC to scratch' : 'planning chunks…';
  } else if (stitching) {
    label = 'Stitching';
    bar = 95 + Math.round((stitch?.progress || 0) * 5);
    sub = 'assembling parts';
  } else {
    label = 'Decoding';
    bar = 10 + Math.round((job.progress || 0) * 85);
    sub = `${chunksDone}/${plannedChunks} chunks`
      + (framesTotal ? ` · ${nf(framesDone)}/${nf(framesTotal)} frames` : '')
      + (fps ? ` · ${Math.round(fps)} fps` : '');
  }
  if (bar == null) bar = Math.round((job.progress || 0) * 100);
  return { label, bar: Math.max(0, Math.min(100, bar)), sub, running, plannedChunks };
}

function fmtDur(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function parentLink(job) {
  const p = job.parent || {};
  if (p.kind === 'render' && p.movieId) return { to: `/movies/${p.movieId}/edit`, label: 'Open movie' };
  if (p.kind === 'asset' && p.tripId && p.assetId) return { to: `/watch/asset/${p.tripId}/${p.assetId}`, label: 'Open clip' };
  if (job.tripId) return { to: `/trips/${job.tripId}`, label: 'Open trip' };
  return null;
}

function JobRow({ job, now }) {
  const [open, setOpen] = useState(false);
  const active = jobIsActive(job);
  // Stream per-task telemetry only for live jobs (skip finished ones to avoid
  // a listener per historical row). Finished rows render from the job doc alone.
  const { tasks } = useJob(active ? job.id : null);
  const live = useMemo(() => deriveLive(job, tasks), [job, tasks]);
  const elapsed = (job.finishedAt || now) - (job.createdAt || now);
  const est = job.cost?.estimatedUsd;
  const actual = jobActualCost(job);
  const link = parentLink(job);
  const statusClass = `cs-jobs-status cs-jobs-status-${job.status}`;

  const confirmCancel = () => {
    if (window.confirm('Kill this job? Its ephemeral tasks will be stopped immediately. This will NOT auto-resubmit.')) {
      cancelJob(job.id);
    }
  };

  return (
    <>
      <tr className={active ? 'cs-jobs-active' : ''}>
        <td>
          <div className="cs-jobs-title">{job.title || job.id}</div>
          <div className="cs-jobs-sub">
            {TYPE_LABEL[job.type] || job.type}
            {link && <> · <Link to={link.to}>{link.label}</Link></>}
          </div>
        </td>
        <td><span className={statusClass}>{live.label}</span></td>
        <td className="num">
          <div className="cs-jobs-bar"><i style={{ width: `${live.bar}%` }} /></div>
          <span className="cs-jobs-pct">{live.bar}%{live.sub ? ` · ${live.sub}` : ''}</span>
        </td>
        <td className="num" title="Live runner tasks (running / planned chunks)">
          {!isFanout(job) ? '—' : active ? (live.plannedChunks ? `${live.running}/${live.plannedChunks}` : live.running) : '—'}
        </td>
        <td className="num">{fmtDur(elapsed)}</td>
        <td className="num">
          <div>{fmtUSD(actual)}{active ? '…' : ''}</div>
          {est != null && <div className="cs-jobs-sub">est {fmtUSD(est)}</div>}
        </td>
        <td className="num cs-jobs-actions">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'View'}</button>
          {isFanout(job) && active && !job.paused && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => pauseJob(job.id)} title="Stop launching new tasks; running tasks finish">Pause</button>
          )}
          {isFanout(job) && active && job.paused && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => resumeJob(job.id)}>Resume</button>
          )}
          {isFanout(job) && active && (
            <button className="cs-btn cs-btn-danger cs-btn-sm" onClick={confirmCancel} title="Stop all tasks now">Kill</button>
          )}
          {!active && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => {
                if (window.confirm('Remove this job from the list? (Telemetry is deleted; this does not affect any files.)')) {
                  deleteJob(job.id);
                }
              }}
              title="Delete this finished job's record"
            >
              Delete
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ background: 'transparent' }}>
            {isFanout(job) ? (
              <JobFanoutGraph jobId={job.id} height={320} title={`${TYPE_LABEL[job.type] || job.type}`} />
            ) : (
              <div className="cs-job-graph">
                <div className="cs-job-head">
                  <div>
                    <strong>{TYPE_LABEL[job.type] || job.type}</strong>
                    <span className={`cs-job-badge cs-job-badge-${job.status}`}>{job.stage || job.status}</span>
                  </div>
                  <div className="cs-job-stats">
                    <span>{Math.round((job.progress || 0) * 100)}%</span>
                    <span>{job.vcpuPerTask || 8} vCPU · {job.gbPerTask || 16} GB</span>
                  </div>
                </div>
                <div className="cs-job-bar"><i style={{ width: `${Math.round((job.progress || 0) * 100)}%` }} /></div>
                <div className="cs-help" style={{ marginTop: 8 }}>
                  Composite encode (HLS ladder + downloadable master) on the always-on task — metered by wallclock.
                  {link && <> <Link to={link.to}>{link.label}</Link>.</>}
                </div>
              </div>
            )}
            {job.status === 'error' && job.message && <div className="cs-job-err" style={{ marginTop: 8 }}>⚠ {job.message}</div>}
            {job.status === 'cancelled' && <div className="cs-help" style={{ marginTop: 8 }}>Cancelled: {job.message || 'by user'}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

// A still grab / develop / grade bake. These run inline on the always-on task
// (no fan-out tasks), so they show a time-extrapolated progress bar and the
// metered/estimated compute cost — no pause/kill controls.
function RenderRow({ render, now }) {
  const rj = render.renderJob || {};
  const active = rj.status === 'queued' || rj.status === 'rendering';
  const bar = renderJobProgress(rj, now);
  const elapsed = (rj.finishedAt || now) - (rj.startedAt || rj.requestedAt || now);
  const cost = rj.costUsd != null ? rj.costUsd : rj.costEstUsd;
  const link = render.tripId ? { to: `/trips/${render.tripId}`, label: 'Open trip' } : null;
  const statusClass = `cs-jobs-status cs-jobs-status-${active ? 'running' : rj.status || 'done'}`;
  return (
    <tr className={active ? 'cs-jobs-active' : ''}>
      <td>
        <div className="cs-jobs-title">{rj.title || render.name || render.id}</div>
        <div className="cs-jobs-sub">
          {RENDER_TYPE_LABEL[rj.kind] || 'Render'}
          {rj.sourceName ? ` · ${rj.sourceName}` : ''}
          {link && <> · <Link to={link.to}>{link.label}</Link></>}
        </div>
      </td>
      <td><span className={statusClass}>{renderJobLabel(rj, now)}</span></td>
      <td className="num">
        <div className="cs-jobs-bar"><i style={{ width: `${bar}%` }} /></div>
        <span className="cs-jobs-pct">{bar}%</span>
      </td>
      <td className="num" title="Inline render on the always-on task (no fan-out tasks)">—</td>
      <td className="num">{fmtDur(elapsed)}</td>
      <td className="num">
        <div>{fmtUSD(cost || 0)}{active ? '…' : ''}</div>
        {rj.costEstUsd != null && rj.costUsd == null && <div className="cs-jobs-sub">est</div>}
      </td>
      <td className="num cs-jobs-actions">
        {link && <Link className="cs-btn cs-btn-ghost cs-btn-sm" to={link.to}>Open</Link>}
      </td>
    </tr>
  );
}

export default function Jobs() {
  const jobs = useJobs(150);
  const renders = useActiveRenders();
  const [now, setNow] = useState(Date.now());
  // Tick the clock while there are active jobs/renders so elapsed/cost stay live.
  const hasActive = (jobs || []).some(jobIsActive) || (renders || []).length > 0;
  React.useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasActive]);

  const summary = useMemo(() => {
    const list = jobs || [];
    const active = list.filter(jobIsActive);
    const liveVcpu = active.reduce((s, j) => s + (j.cost?.vcpuSeconds != null ? (j.cost?.tasks || 0) * (j.vcpuPerTask || 8) : 0), 0);
    const spentTotal = list.reduce((s, j) => s + jobActualCost(j), 0);
    const erroredCount = list.filter((j) => j.status === 'error').length;
    const renderCount = (renders || []).length;
    return {
      activeCount: active.length + renderCount,
      total: list.length + renderCount,
      liveVcpu: liveVcpu + renderCount * 8,
      spentTotal,
      erroredCount,
    };
  }, [jobs, renders]);

  const [clearing, setClearing] = useState(false);
  const onClearErrored = async () => {
    if (!summary.erroredCount) return;
    if (!window.confirm(`Delete ${summary.erroredCount} errored job(s) from the list? This only removes their records, not any files.`)) return;
    setClearing(true);
    try {
      await clearErroredJobs();
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="cs-page">
      <div className="cs-section-head">
        <h1>Live processes &amp; jobs</h1>
        {summary.erroredCount > 0 && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClearErrored} disabled={clearing}>
            {clearing ? 'Clearing…' : `Clear errored (${summary.erroredCount})`}
          </button>
        )}
      </div>
      <p className="cs-section-sub">
        Every media job the app runs — RED decode, RAW re-decode, movie publishing (HLS ladder +
        downloadable master), plus inline photo work (frame-grab stills, RAW develop, grade exports).
        Watch them in real time and see metered cost per job. Fan-out decode jobs run on short-lived
        runner tasks you can pause/kill; publishes and photo renders run on the always-on task and are
        metered by wallclock. Killed jobs report a reason and are never auto-resubmitted.
      </p>

      <div className="cs-jobs-summary">
        <div className="cs-jobs-stat"><span>{summary.activeCount}</span><label>active jobs</label></div>
        <div className="cs-jobs-stat"><span>{summary.liveVcpu}</span><label>vCPU running</label></div>
        <div className="cs-jobs-stat"><span>{fmtUSD(summary.spentTotal)}</span><label>metered total</label></div>
        <div className="cs-jobs-stat"><span>{summary.total}</span><label>jobs tracked</label></div>
      </div>

      {jobs === null ? (
        <div className="cs-empty">Loading…</div>
      ) : jobs.length === 0 && renders.length === 0 ? (
        <div className="cs-empty">No jobs yet. Decoding a RED clip, baking a still, or publishing a movie will appear here.</div>
      ) : (
        <table className="cs-table cs-jobs-table">
          <thead>
            <tr>
              <th>Job</th><th>Status</th><th className="num">Progress</th><th className="num">Tasks</th>
              <th className="num">Elapsed</th><th className="num">Cost</th><th className="num">Controls</th>
            </tr>
          </thead>
          <tbody>
            {renders.map((r) => <RenderRow key={`${r.tripId}/${r.id}`} render={r} now={now} />)}
            {jobs.map((j) => <JobRow key={j.id} job={j} now={now} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}
